<template>
  <Dialog
    :model-value="visible"
    :title="headerTitle"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >   
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M6.21 13.97L7.41 16.04C7.49 16.17 7.64 16.22 7.78 16.17L9.27 15.57C9.58 15.81 9.91 16.01 10.28 16.16L10.5 17.75C10.53 17.89 10.65 18 10.8 18H13.2C13.35 18 13.47 17.89 13.5 17.74L13.72 16.15C14.08 16 14.42 15.8 14.73 15.56L16.22 16.16C16.36 16.21 16.51 16.16 16.59 16.03L17.79 13.96C17.87 13.83 17.83 13.67 17.72 13.57L16.45 12.58C16.48 12.39 16.49 12.19 16.49 12C16.49 11.8 16.47 11.61 16.45 11.41L17.72 10.42C17.83 10.33 17.87 10.16 17.79 10.03L16.59 7.96C16.51 7.83 16.36 7.78 16.22 7.83L14.73 8.43C14.42 8.19 14.09 7.99 13.72 7.84L13.5 6.25C13.47 6.11 13.35 6 13.2 6H10.8C10.65 6 10.53 6.11 10.5 6.26L10.28 7.85C9.92 8 9.57 8.19 9.27 8.43L7.78 7.83C7.64 7.78 7.49 7.83 7.41 7.96L6.21 10.03C6.13 10.16 6.17 10.32 6.28 10.42L7.55 11.41C7.52 11.61 7.5 11.8 7.5 12C7.5 12.2 7.52 12.39 7.54 12.59L6.27 13.58C6.16 13.68 6.13 13.84 6.21 13.97ZM12 10.29C12.94 10.29 13.71 11.06 13.71 12C13.71 12.94 12.94 13.71 12 13.71C11.06 13.71 10.29 12.94 10.29 12C10.29 11.06 11.06 10.29 12 10.29ZM19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM19 19H5V5H19V19Z" fill="white"/>
      </svg>
    </template>
    <div class="dialog-scrollable-content">
      <!-- View 1: Manage Assignees -->
      <div v-show="currentView === 'manage'" class="dialog-content">
      <!-- Current Workflow Status -->
      <div class="status-section">
        <p class="section-title">Current Workflow Status</p>
        <div class="workflow-toggle">
          <span class="toggle-label" :class="{ disabled: isSwitcherDisabled }">Draft</span>
          <el-tooltip
            :content="disabledTooltipMessage"
            :disabled="!isSwitcherDisabled"
            placement="top"
            effect="dark"
          >
            <div 
              class="toggle-switch" 
              :class="{ active: isActive, disabled: isSwitcherDisabled }" 
              @click="isSwitcherDisabled ? null : toggleStatus()"
            >
              <div class="toggle-handle"></div>
            </div>
          </el-tooltip>
          <span class="toggle-label" :class="{ disabled: isSwitcherDisabled }">Active</span>
        </div>
      </div>

      <!-- Draft Placeholder - Shown when workflow is draft -->
      <div v-if="!isActive" class="draft-placeholder">
        <div class="placeholder-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M24 4C12.96 4 4 12.96 4 24C4 35.04 12.96 44 24 44C35.04 44 44 35.04 44 24C44 12.96 35.04 4 24 4ZM24 40C15.18 40 8 32.82 8 24C8 15.18 15.18 8 24 8C32.82 8 40 15.18 40 24C40 32.82 32.82 40 24 40ZM22 22V14H26V22H22ZM22 34V26H26V34H22Z" fill="#9CA3AF"/>
          </svg>
        </div>
        <p class="placeholder-title">Assignee Configuration Unavailable</p>
        <p class="placeholder-description">
          To configure patient assignments, please activate this {{ isProgram ? 'program' : 'workflow' }} by toggling the switch above to "Active".
        </p>
      </div>

      <!-- Assigned Patients - Only shown when workflow is active -->
      <div v-if="isActive" class="patients-section mb-[20px]">
          <div class="patients-header">
            <p class="section-title">Assigned Patients</p>
          </div>

          <!-- Patient List -->
          <div class="patient-list">
            <!-- Empty State -->
            <div v-if="patients.length === 0" class="empty-state">
              <img src="@/assets/MyWorkBench/Workflow/program_no_assignee.svg" alt="Empty State" />
            </div>

            <!-- Patient List -->
            <transition-group v-else name="patient-list" tag="div" class="patient-list-container">
              <div v-for="patient in patients" :key="patient.id" class="patient-item">
                <div class="patient-avatar">
                  <img v-if="patient.avatar" :src="patient.avatar" :alt="patient.name" />
                  <div v-else class="avatar-placeholder">{{ patient.initials }}</div>
                </div>
                <div class="patient-info">
                  <!-- Patient Name + Actions Row -->
                  <div class="patient-header">
                    <div class="flex flex-col">
                      <div class="patient-name">{{ patient.name }}</div>
                      <div class="patient-meta">
                        <div class="patient-id-container">
                          <span class="patient-id">ID: #{{ patient.id }}</span>
                        </div>
                        <span class="patient-added">Added {{ patient.addedTime }}</span>
                      </div>
                    </div>
                    <div class="patient-actions-group">
                      <!-- Pause/Resume Button - Hide when completed -->
                      <FoldableButton
                        v-if="patient.status !== 'completed'"
                        :text="patient.isPaused ? 'Resume Usage' : 'Pause Usage'"
                        built_in_theme="default"
                        @click="togglePause(patient)"
                      >
                        <template #prefix>
                          <svg v-if="patient.isPaused" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M9 0C4.03125 0 0 4.03125 0 9C0 13.9688 4.03125 18 9 18C13.9688 18 18 13.9688 18 9C18 4.03125 13.9688 0 9 0ZM9 16.3125C4.96875 16.3125 1.6875 13.0312 1.6875 9C1.6875 4.96875 4.96875 1.6875 9 1.6875C13.0312 1.6875 16.3125 4.96875 16.3125 9C16.3125 13.0312 13.0312 16.3125 9 16.3125ZM6.75 5.0625V12.9375L13.5 9L6.75 5.0625Z" fill="currentColor"/>
                          </svg>
                          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M9 0C4.03125 0 0 4.03125 0 9C0 13.9688 4.03125 18 9 18C13.9688 18 18 13.9688 18 9C18 4.03125 13.9688 0 9 0ZM9 16.3125C4.96875 16.3125 1.6875 13.0312 1.6875 9C1.6875 4.96875 4.96875 1.6875 9 1.6875C13.0312 1.6875 16.3125 4.96875 16.3125 9C16.3125 13.0312 13.0312 16.3125 9 16.3125ZM7.3125 5.625H8.4375V12.375H7.3125V5.625ZM9.5625 5.625H10.6875V12.375H9.5625V5.625Z" fill="currentColor"/>
                          </svg>
                        </template>
                      </FoldableButton>
                      <!-- Delete Button -->
                      <FoldableButton text="Remove Patient" built_in_theme="red" @click="deletePatient(patient)">
                        <template #prefix>
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                            <path d="M13 1.30833L11.6917 0L6.5 5.19167L1.30833 0L0 1.30833L5.19167 6.5L0 11.6917L1.30833 13L6.5 7.80833L11.6917 13L13 11.6917L7.80833 6.5L13 1.30833Z" fill="currentColor"/>
                          </svg>
                        </template>
                      </FoldableButton>
                    </div>
                  </div>
                  <!-- Two Progress Bars Side by Side -->
                  <div class="progress-section">
                    <!-- Progress Bar 1 - Program Steps -->
                    <div class="progress-item">
                      <p class="progress-label">{{ patient.workflowProgress }}/{{ programStepsTotal }} steps completed ({{ programStepsTotal > 0 ? Math.round(patient.workflowProgress / programStepsTotal * 100) : 0 }}%)</p>
                      <div class="progress-bar-container">
                        <div class="progress-bar" :style="{ width: programStepsTotal > 0 ? (patient.workflowProgress / programStepsTotal * 100) + '%' : '0%' }"></div>
                      </div>
                    </div>
                    <!-- Progress Bar 2 - Date-based Progress -->
                    <div class="progress-item">
                      <p class="progress-label">{{ patient.remainingDays }}/{{ patient.totalDays }} days remaining ({{ patient.totalDays > 0 ? Math.round((patient.totalDays - patient.remainingDays) / patient.totalDays * 100) : 0 }}%)</p>
                      <div class="progress-bar-container">
                        <div class="progress-bar" :style="{ width: patient.totalDays > 0 ? ((patient.totalDays - patient.remainingDays) / patient.totalDays * 100) + '%' : '0%' }"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition-group>
          </div>
        </div>
      </div>

      <!-- View 2: Add Patient to Program -->
      <div v-show="currentView === 'add-patient'" class="dialog-content">
        <div class="content-section">
          <!-- Select Patients Field -->
          <div class="field-group">
            <label class="field-label">Select Patients</label>

            <!-- MultiSelector Component -->
            <MultiSelector
              v-model="selectedPatientValues"
              :options="patientOptions"
              :remote_search="true"
              :remote_search_pagination="true"
              :remote_search_has_more="hasMoreResults"
              :remote_search_load_more_loading="isLoading"
              :loading="isLoading && allPatients.length === 0"
              placeholder="Select patients"
              size="medium"
              dropdown_max_height="300px"
              @search="handlePatientSearch"
              @remote-search-load-more="handleLoadMorePatients"
            >
              <template #option="{ option, is_selected }">
                <div class="patient-option-custom" :class="{ disabled: isPatientOptionDisabled(option) }">
                  <div class="patient-option-info">
                    <span class="patient-option-name">{{ option.label }}</span>
                    <span class="patient-option-meta">ID: #{{ option.value }}</span>
                  </div>
                  <div v-if="isPatientOptionDisabled(option)" class="patient-option-disabled-reason">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="#D10000"/>
                    </svg>
                    <span>Max enrollment reached</span>
                  </div>
                </div>
              </template>
            </MultiSelector>
          </div>
        </div>
      </div>

      <!-- View 3: Confirm Adding Patients -->
      <div v-show="currentView === 'confirm'" class="dialog-content">
        <p class="confirmation-message">
          Please confirm that you would like to add these patients to program. This will create an order of this program for patients.
        </p>

        <!-- Patient List -->
        <div class="patient-list-container">
          <div class="confirm-patient-list">
            <div v-for="(patient, index) in selectedPatients" :key="patient.id" class="confirm-patient-item">
              <div class="patient-avatar">
                <div v-if="patient.avatar">
                  <img :src="patient.avatar" :alt="patient.name" />
                </div>
                <div v-else class="avatar-placeholder">
                  {{ patient.initials }}
                </div>
              </div>
              <div class="patient-info">
                <div class="flex">
                  <div class="confirm-patient-name">{{ patient.name }} (Patient ID: {{ patient.id }})</div>
                </div>
                <div class="patient-meta-rows">
                  Bio Gender: {{ patient.gender || 'N/A' }} | DOB: {{ patient.dob || 'N/A' }}
                </div>
              </div>
              <button class="confirm-delete-button" @click="removePatientFromConfirm(index)">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M13 1.30833L11.6917 0L6.5 5.19167L1.30833 0L0 1.30833L5.19167 6.5L0 11.6917L1.30833 13L6.5 7.80833L11.6917 13L13 11.6917L7.80833 6.5L13 1.30833Z" fill="#D10000"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <!-- Footer for Manage View -->
      <template v-if="currentView === 'manage'">
        <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="closeDialog">Cancel</ResponsiveButton>
        <template v-if="originalPatients.length === 0 && !hasUnsavedChanges">
          <el-tooltip
            :content="isMaxEnrollmentReached ? `Maximum enrollment (${maxEnrollment}) reached. Remove completed patients to add more.` : ''"
            :disabled="!isMaxEnrollmentReached"
            placement="top"
          >
            <span>
              <ResponsiveButton button_type="filled" built_in_theme="dark" size="medium" :disabled="!isActive || isMaxEnrollmentReached" @click="openAddPatientState">Add Patients</ResponsiveButton>
            </span>
          </el-tooltip>
        </template>
        <template v-else-if="isActive">
          <el-tooltip
            :content="isMaxEnrollmentReached ? `Maximum enrollment (${maxEnrollment}) reached. Remove completed patients to add more.` : ''"
            :disabled="!isMaxEnrollmentReached"
            placement="top"
          >
            <span>
              <ResponsiveButton button_type="outlined" built_in_theme="dark" size="medium" :disabled="isMaxEnrollmentReached" @click="openAddPatientState">Add New Patients</ResponsiveButton>
            </span>
          </el-tooltip>
        </template>
      </template>

      <!-- Footer for Add Patient View -->
      <template v-else-if="currentView === 'add-patient'">
        <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="cancelAddPatient">Cancel</ResponsiveButton>
        <ResponsiveButton button_type="filled" built_in_theme="dark" size="medium" @click="saveSelection">Save</ResponsiveButton>
      </template>

      <!-- Footer for Confirm View -->
      <template v-else-if="currentView === 'confirm'">
        <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="handleConfirmBack">Back</ResponsiveButton>
        <ResponsiveButton button_type="filled" built_in_theme="dark" size="medium" @click="handleConfirmAccept">Accept</ResponsiveButton>
      </template>
    </template>
  </Dialog>

  <!-- Remove Patient Confirmation Dialog -->
  <RemovePatientConfirmationDialog
    :visible="showRemoveDialog"
    :patient="patientToRemove"
    :program-id="programId"
    @close="showRemoveDialog = false"
    @success="handleRemoveSuccess"
  />
</template>

<script>
import RemovePatientConfirmationDialog from '@/views/my-workbench-new/workflow/Dialogs/RemovePatientConfirmationDialog.vue';
import { notify } from '@/utils/notify';
import { getPatientListApi } from '@/api/patientList';

export default {
  name: 'ManageAssigneesDialog',
  components: {
    RemovePatientConfirmationDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    programId: {
      type: String,
      required: true
    },
    workflowStatus: {
      type: String,
      default: 'draft' // 'draft' or 'active'
    },
    maxEnrollment: {
      type: Number,
      default: null
    },
    isProgram: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'save', 'status-changed', 'save-workflow-before-activate'],
  data() {
    return {
      isActive: false,
      currentView: 'manage', // 'manage', 'add-patient', 'confirm'
      isLoadingPatients: false,
      patients: [],
      originalPatients: [], // Track original state for change detection
      // Remove patient state
      showRemoveDialog: false,
      patientToRemove: {},
      // Add patient view data
      selectedPatients: [],
      allPatients: [],
      currentPage: 1,
      perPage: 20,
      totalResults: 0,
      isLoading: false,
      hasMoreResults: true,
      searchDebounceTimer: null,
      programStepsTotal: 0,
      currentSearchQuery: ''
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },

    /**
     * Check if there are unsaved changes
     * Compares current patients list with original list
     */
    hasUnsavedChanges() {
      if (this.patients.length !== this.originalPatients.length) {
        return true;
      }
      
      // Check if any patient has different status or if patient IDs differ
      for (let i = 0; i < this.patients.length; i++) {
        const current = this.patients[i];
        const original = this.originalPatients.find(p => p.id === current.id);
        
        if (!original || current.isPaused !== original.isPaused) {
          return true;
        }
      }
      
      return false;
    },
    existingPatientIds() {
      return this.patients.map(p => p.id);
    },
    headerTitle() {
      if (this.currentView === 'manage') return 'Manage Program Assignees';
      if (this.currentView === 'add-patient') return 'Add Patients to Program';
      if (this.currentView === 'confirm') return 'Add New Program Assignees';
      return '';
    },
    filteredPatients() {
      // Filter out patients that are already assigned
      return this.allPatients.filter(patient => {
        return !this.existingPatientIds.includes(patient.id) &&
               !this.existingPatientIds.includes(String(patient.id));
      });
    },
    patientOptions() {
      // Transform filtered patients to MultiSelector options
      return this.filteredPatients.map(patient => ({
        value: String(patient.id),
        label: patient.name,
        disabled: false,
        originalData: patient
      }));
    },
    selectedPatientValues: {
      get() {
        // Return selected patient IDs as strings
        return this.selectedPatients.map(p => String(p.id));
      },
      set(values) {
        // Convert selected IDs back to patient objects
        this.selectedPatients = values
          .map(id => this.allPatients.find(p => String(p.id) === id))
          .filter(p => p); // Remove undefined values
      }
    },
    nonCompletedPatientCount() {
      // Count patients who are not completed (active or paused)
      return this.patients.filter(p => p.status !== 'completed').length;
    },
    isMaxEnrollmentReached() {
      // Check if max enrollment is reached (only if maxEnrollment is set)
      if (!this.maxEnrollment) return false;
      return this.nonCompletedPatientCount >= this.maxEnrollment;
    },
    availableSlots() {
      // Calculate available slots
      if (!this.maxEnrollment) return null;
      return Math.max(0, this.maxEnrollment - this.nonCompletedPatientCount);
    },
    isSwitcherDisabled() {
      // Disable switcher if there are assigned patients
      // No need to check hasUnsavedChanges since we use real-time APIs
      return this.patients.length > 0;
    },
    disabledTooltipMessage() {
      // Generate tooltip message based on why switcher is disabled
      if (this.patients.length > 0) {
        return 'Cannot change status: program has assigned patients. Remove all patients before changing to Draft.';
      }
      return '';
    },
    canAddMorePatients() {
      // Check if we can add more patients based on max enrollment
      return !this.isMaxEnrollmentReached;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Reset to manage view and load program assignees when dialog opens
        this.currentView = 'manage';
        this.isActive = this.workflowStatus === 'active';
        if (this.isActive) {
          this.loadAssignees();
        }
      } else {
        // Clear debounce timer when dialog closes
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
          this.searchDebounceTimer = null;
        }
      }
    },
    workflowStatus(newStatus) {
      // Sync isActive state when workflowStatus prop changes from canvas
      this.isActive = newStatus === 'active';
      
      // Load assignees if workflow becomes active
      if (this.isActive && this.visible) {
        this.loadAssignees();
      }
    }
  },
  mounted() {
    console.log('ManageAssigneesDialog mounted');
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },

    
    /**
     * Toggle workflow status between Draft and Active
     * Calls validate+activate or deactivate API immediately
     */
    async toggleStatus() {
      const newStatus = !this.isActive;
      
      if (newStatus) {
        // Switching from draft to active: validate first, then activate
        await this.activateWorkflow();
      } else {
        // Switching from active to draft: check if workflow has assigned patients
        if (this.patients.length > 0) {
          notify({
            title: 'Cannot Deactivate',
            message: 'Cannot change workflow status to Draft while it has assigned patients. Please remove all assigned patients first.',
            type: 'warning',
            showClose: true
          });
          return;
        }
        
        // Deactivate workflow
        await this.deactivateWorkflow();
      }
    },
    
    /**
     * Validate and activate workflow
     * First saves the workflow, then validates, then activates
     */
    async activateWorkflow() {
      try {
        // Step 1: Request workflow save from parent (WorkflowCanvas)
        const savePromise = new Promise((resolve, reject) => {
          this.$emit('save-workflow-before-activate', {
            onSuccess: () => resolve(true),
            onError: (error) => reject(error)
          });
        });

        await savePromise;
        console.log('Workflow saved successfully before activation');

        // Step 2: Import API functions
        const { validateWorkflowApi, activateWorkflowApi } = await import('@/api/workflowManage');

        // Step 3: Validate workflow
        const validateResponse = await validateWorkflowApi(this.programId);
        
        if (validateResponse.status !== 200) {
          const validationData = validateResponse.data || validateResponse;
          let errorMessages = '';
          const errorList = validationData.errors || [];
          errorList.forEach(e=>{
            errorMessages += e.message + '\n';
          })
          notify({
            title: 'Validation Failed',
            message: errorMessages || 'Workflow validation failed',
            type: 'error',
            showClose: true
          });
          return;
        }
        
        // Show warnings if any
        const validationData = validateResponse.data || validateResponse;
        if (validationData.warnings && validationData.warnings.length > 0) {
          const warningMessages = validationData.warnings.join('\n');
          notify({
            title: 'Validation Warnings',
            message: warningMessages,
            type: 'warning',
            showClose: true
          });
        }
        
        // Step 4: Activate workflow
        const activateResponse = await activateWorkflowApi(this.programId);
        
        // Update local state
        this.isActive = true;
        
        // Emit status change to parent
        this.$emit('status-changed', { status: 'active', data: activateResponse.data });
        
        notify({
          title: 'Success',
          message: 'Workflow activated successfully!',
          type: 'success',
          showClose: true
        });
        
        // Load assignees for active workflow
        await this.loadAssignees();
        
      } catch (error) {
        console.error('Error activating workflow:', error);
        notify({
          title: 'Error',
          message: `Failed to activate workflow: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
    
    /**
     * Deactivate workflow
     */
    async deactivateWorkflow() {
      try {
        const { deactivateWorkflowApi } = await import('@/api/workflowManage');
        
        const response = await deactivateWorkflowApi(this.programId);
        
        // Update local state
        this.isActive = false;
        
        // Emit status change to parent
        this.$emit('status-changed', { status: 'draft', data: response.data });
        
        notify({
          title: 'Success',
          message: 'Workflow deactivated successfully!',
          type: 'success',
          showClose: true
        });
        
        // Clear patients list
        this.patients = [];
        
      } catch (error) {
        console.error('Error deactivating workflow:', error);
        notify({
          title: 'Error',
          message: `Failed to deactivate workflow: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },

    /**
     * Calculate date-based progress
     * @param {string} startDate - ISO date string from API
     * @param {string} endDate - ISO date string from API
     * @returns {Object} { totalDays, remainingDays, elapsedDays }
     */
    calculateDateProgress(startDate, endDate) {
      if (!startDate || !endDate) {
        return { totalDays: 0, remainingDays: 0, elapsedDays: 0 };
      }
      
      // Convert UTC dates to local time
      const start = new Date(startDate);
      const end = new Date(endDate);
      const now = new Date();
      
      // Calculate total days between start and end
      const totalMs = end.getTime() - start.getTime();
      const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
      
      // Scenario 1: Current time is before start date
      if (now < start) {
        return {
          totalDays,
          remainingDays: totalDays, // All days are remaining
          elapsedDays: 0 // No days elapsed yet
        };
      }
      
      // Scenario 2: Current time is after end date
      if (now > end) {
        return {
          totalDays,
          remainingDays: 0, // No days remaining
          elapsedDays: totalDays // All days elapsed
        };
      }
      
      // Scenario 3: Current time is between start and end (normal case)
      const elapsedMs = now.getTime() - start.getTime();
      const elapsedDays = Math.ceil(elapsedMs / (1000 * 60 * 60 * 24));
      
      const remainingMs = end.getTime() - now.getTime();
      const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
      
      return {
        totalDays,
        remainingDays,
        elapsedDays
      };
    },

    /**
     * Load assignees from API for this program
     */
    async loadAssignees() {
      try {
        this.isLoadingPatients = true;
        const { getWorkflowAssigneesApi } = await import('@/api/workflowManage');
        
        const response = await getWorkflowAssigneesApi(this.programId);
        console.log('Loaded assignees response:', response);
        
        // Store program steps total
        this.programStepsTotal = response.data?.program_steps_total || 0;
        
        // Transform API response to component format
        this.patients = (response.data?.assignments || []).map(assignment => {
          // If completed, set progress to 100% (executed = total)
          const isCompleted = assignment.status === 'completed';
          const workflowProgress = isCompleted ? this.programStepsTotal : (assignment.program_steps_executed || 0);
          
          // Calculate date-based progress for second progress bar
          const dateProgress = this.calculateDateProgress(
            assignment.program_start_date,
            assignment.program_end_date
          );
          
          return {
            id: assignment.patient_id,
            name: assignment.patient_name || `Patient ${assignment.patient_id}`,
            avatar: assignment.patient_avatar || null,
            initials: this.getInitials(assignment.patient_name || `Patient ${assignment.patient_id}`),
            addedTime: this.formatRelativeTime(assignment.assigned_at),
            status: assignment.status, // 'running', 'pending', 'paused', or 'completed'
            isPaused: assignment.status === 'paused',
            workflowProgress: workflowProgress,
            programStartDate: assignment.program_start_date,
            programEndDate: assignment.program_end_date,
            totalDays: dateProgress.totalDays,
            remainingDays: dateProgress.remainingDays,
            taskProgress: dateProgress.elapsedDays // Use elapsed days for progress bar
          };
        });

        // Store original state for change detection
        this.originalPatients = JSON.parse(JSON.stringify(this.patients));

        console.log('Loaded patients:', this.patients);
        console.log('Program steps total:', this.programStepsTotal);
        console.log('Non-completed count:', this.nonCompletedPatientCount);

      } catch (error) {
        console.error('Error loading assignees:', error);
        notify({
          title: 'Error',
          message: `Failed to load assignees: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      } finally {
        this.isLoadingPatients = false;
      }
    },
    
    /**
     * Toggle patient pause/resume status
     * Calls API immediately for real-time update
     */
    async togglePause(patient) {
      try {
        const { pausePatientAssignmentApi, resumePatientAssignmentApi } = await import('@/api/workflowManage');
        
        if (patient.isPaused) {
          // Resume the patient
          await resumePatientAssignmentApi(this.programId, patient.id);
          patient.isPaused = false;
          patient.status = 'running'; // Update to running status
          
          notify({
            title: 'Success',
            message: `Patient ${patient.name} resumed successfully`,
            type: 'success',
            showClose: true
          });
        } else {
          // Pause the patient
          await pausePatientAssignmentApi(this.programId, patient.id);
          patient.isPaused = true;
          patient.status = 'paused';
          
          notify({
            title: 'Success',
            message: `Patient ${patient.name} paused successfully`,
            type: 'success',
            showClose: true
          });
        }
        
        // Emit save event to parent for dynamic tracking
        this.$emit('save', {
          isActive: this.isActive,
          patients: this.patients
        });
        
      } catch (error) {
        console.error('Error toggling pause status:', error);
        notify({
          title: 'Error',
          message: `Failed to ${patient.isPaused ? 'resume' : 'pause'} patient: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
    
    /**
     * Get initials from name
     */
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    /**
     * Format timestamp to relative time (e.g., "2 days ago")
     */
    formatRelativeTime(timestamp) {
      if (!timestamp) return 'Recently';

      const now = new Date();
      const date = new Date(timestamp);
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);

      if (diffSecs < 60) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    },

    /**
     * Open add patient view (called from header buttons)
     */
    openAddPatientState() {
      this.currentView = 'add-patient';
      this.resetAddPatientForm();
      // Load initial patients to populate the dropdown
      this.$nextTick(() => {
        this.handlePatientSearch('');
      });
    },

    /**
     * Open add patient view (called from footer button)
     */
    openAddPatientDialog() {
      this.currentView = 'add-patient';
      this.resetAddPatientForm();
      // Load initial patients to populate the dropdown
      this.$nextTick(() => {
        this.handlePatientSearch('');
      });
    },

    /**
     * Cancel add patient and return to manage view
     */
    cancelAddPatient() {
      this.currentView = 'manage';
      this.resetAddPatientForm();
    },

    /**
     * Save patient selection - show confirm view
     */
    saveSelection() {
      this.currentView = 'confirm';
    },

    /**
     * Handle back from confirmation view
     */
    handleConfirmBack() {
      this.currentView = 'add-patient';
    },

    /**
     * Handle accept from confirmation view
     * Returns to manage view WITHOUT closing the dialog
     */
    async handleConfirmAccept() {
      try {
        console.log('Adding patients to program:', this.selectedPatients);

        // Filter out patients that already exist
        const newPatients = this.selectedPatients.filter(patient => {
          return !this.patients.some(p => p.id === patient.id);
        });
        
        if (newPatients.length === 0) {
          notify({
            title: 'Info',
            message: 'All selected patients are already assigned',
            type: 'info',
            showClose: true
          });
          this.currentView = 'manage';
          this.resetAddPatientForm();
          return;
        }

        const { batchAssignPatientsToWorkflowApi } = await import('@/api/workflowManage');
        
        // Use batch API to assign all patients at once
        const response = await batchAssignPatientsToWorkflowApi(this.programId, {
          patient_ids: newPatients.map(p => parseInt(p.id))
        });
        
        console.log('Batch assign response:', response);
        
        // Add all successfully assigned patients to local list
        if (response.data?.results && Array.isArray(response.data.results)) {
          response.data.results.forEach(result => {
            if (result.success) {
              const patient = newPatients.find(p => parseInt(p.id) === result.patient_id);
              if (patient) {
                this.patients.push({
                  id: patient.id,
                  name: patient.name,
                  avatar: null,
                  initials: patient.initials,
                  addedTime: 'just now',
                  status: result.status || 'pending',
                  workflowProgress: 0,
                  programStartDate: result.program_start_date,
                  programEndDate: result.program_end_date,
                  totalDays: 0,
                  remainingDays: 0,
                  taskProgress: 0,
                  isPaused: false
                });
              }
            }
          });
        }
        
        const assignedCount = response.data?.assigned || 0;
        const failedCount = response.data?.failed || 0;
        
        if (assignedCount > 0) {
          notify({
            title: 'Success',
            message: `${assignedCount} patient${assignedCount > 1 ? 's' : ''} added successfully!`,
            type: 'success',
            showClose: true
          });
        }
        
        if (failedCount > 0) {
          notify({
            title: 'Warning',
            message: `${failedCount} patient${failedCount > 1 ? 's' : ''} failed to add`,
            type: 'warning',
            showClose: true
          });
        }
        
        // Emit save event to parent for dynamic tracking
        this.$emit('save', {
          isActive: this.isActive,
          patients: this.patients
        });
        
        // Update original patients to reflect saved state
        this.originalPatients = JSON.parse(JSON.stringify(this.patients));

        // Return to manage view WITHOUT closing dialog
        this.currentView = 'manage';
        this.resetAddPatientForm();
        // Dialog stays open - user must click Cancel to close

      } catch (error) {
        console.error('Error adding patients to program:', error);
        notify({
          title: 'Error',
          message: `Failed to add patients: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },

    /**
     * Load patients from API
     */
    async loadPatients(reset = false) {
      if (this.isLoading) return;

      // Check if we've already loaded all results
      if (!reset && !this.hasMoreResults) return;

      try {
        this.isLoading = true;

        // Reset pagination if needed
        if (reset) {
          this.currentPage = 1;
          this.allPatients = [];
          this.hasMoreResults = true;
        }

        const response = await getPatientListApi({
          search_patient_field: this.currentSearchQuery,
          search_patient_type: 'patient_name',
          page: String(this.currentPage),
          perPage: String(this.perPage)
        });

        if (response && response.patients && Array.isArray(response.patients)) {
          const patients = response.patients.map(patient => {
            const nameParts = `${patient.patient_first_name} ${patient.patient_last_name}`.trim().split(' ').filter(n => n);
            const initials = nameParts.map(n => n[0].toUpperCase()).join('');

            return {
              id: patient.patient_id,
              name: `${patient.patient_first_name} ${patient.patient_last_name}`.trim(),
              initials: initials,
              accessionId: 'PN-' + patient.patient_id,
              gender: 'Male',
              dob: 'Jan-05-1979'
            };
          });

          if (reset) {
            this.allPatients = patients;
          } else {
            this.allPatients = [...this.allPatients, ...patients];
          }

          this.totalResults = response.total_count || 0;
          const loadedCount = this.allPatients.length;
          this.hasMoreResults = loadedCount < this.totalResults;
        }
      } catch (error) {
        console.error('Error loading patients:', error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Reset add patient form
     */
    resetAddPatientForm() {
      this.selectedPatients = [];
      this.allPatients = [];
      this.currentPage = 1;
      this.totalResults = 0;
      this.hasMoreResults = true;
      this.currentSearchQuery = '';

      // Clear debounce timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    },

    /**
     * Handle search from MultiSelector
     */
    handlePatientSearch(query) {
      console.log('Search event triggered with query:', query);
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      this.searchDebounceTimer = setTimeout(async () => {
        this.currentSearchQuery = query;
        await this.loadPatients(true);
      }, 500);
    },

    /**
     * Handle load more from MultiSelector pagination
     */
    async handleLoadMorePatients(query) {
      console.log('Load more event triggered with query:', query);
      console.log('Current state - isLoading:', this.isLoading, 'hasMoreResults:', this.hasMoreResults, 'currentPage:', this.currentPage);
      if (this.isLoading || !this.hasMoreResults) {
        console.log('Load more blocked');
        return;
      }

      this.currentPage++;
      await this.loadPatients(false);
    },

    /**
     * Check if patient option should be disabled based on max enrollment
     */
    isPatientOptionDisabled(option) {
      // If no max enrollment set, never disable
      if (!this.maxEnrollment) return false;

      // If already selected, allow unselecting
      if (this.selectedPatientValues.includes(option.value)) return false;

      // Calculate total if we add this patient
      const totalIfAdded = this.selectedPatients.length + this.nonCompletedPatientCount;

      // Disable if adding would exceed max enrollment
      return totalIfAdded >= this.maxEnrollment;
    },

    /**
     * Remove patient from confirmation list
     */
    removePatientFromConfirm(index) {
      this.selectedPatients.splice(index, 1);
      // If no patients left, go back to add-patient view
      if (this.selectedPatients.length === 0) {
        this.currentView = 'add-patient';
      }
    },

    /**
     * Remove patient from program
     * Shows confirmation dialog before removal
     */
    deletePatient(patient) {
      // Set patient data and show confirmation dialog
      this.patientToRemove = patient;
      this.showRemoveDialog = true;
    },

    /**
     * Handle successful patient removal from confirmation dialog
     */
    async handleRemoveSuccess() {
      // Close the confirmation dialog
      this.showRemoveDialog = false;
      
      // Remove from local list
      const index = this.patients.findIndex(p => p.id === this.patientToRemove.id);
      if (index > -1) {
        this.patients.splice(index, 1);
      }
      
      // Emit save event to parent for dynamic tracking
      this.$emit('save', {
        isActive: this.isActive,
        patients: this.patients
      });
    }
  }
};
</script>

<style scoped>
/* Scrollable content container */
.dialog-scrollable-content {
  max-height: calc(80vh - 120px);
  overflow-y: auto;
  flex: 1;
}

/* Status Section */
.status-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 16px 12px;
  border-bottom: 1px dashed var(--DividerGray, #C4C4C4);
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #4A4A4A;
  margin: 0;
}

.workflow-toggle {
  display: flex;
  align-items: center;
  gap: 7px;
}

.toggle-label {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
  transition: color 0.3s ease;
}

.toggle-label.disabled {
  color: #C9C9C9;
}

.toggle-switch {
  position: relative;
  width: 40px;
  height: 22px;
  background: #F9FBFD;
  border: 1px solid #374957;
  border-radius: 13px;
  cursor: pointer;
  padding: 2px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.toggle-switch.active {
  background: #004879;
  border-color: #004879;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background: #F3F4F6;
  border-color: #C9C9C9;
}

.toggle-switch.disabled.active {
  background: #7BA5C9;
  border-color: #7BA5C9;
}

.toggle-handle {
  width: 18px;
  height: 18px;
  background: #374957;
  border-radius: 50%;
  transition: all 0.3s ease;
  transform: translateX(0);
}

.toggle-switch.active .toggle-handle {
  background: #FFFFFF;
  transform: translateX(18px);
}

.toggle-switch.disabled .toggle-handle {
  background: #9CA3AF;
}

.toggle-switch.disabled.active .toggle-handle {
  background: #E5E7EB;
}

/* Draft Placeholder */
.draft-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  min-height: 300px;
}

.placeholder-icon {
  margin-bottom: 24px;
}

.placeholder-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #4A4A4A;
  margin: 0 0 12px 0;
}

.placeholder-description {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #666666;
  margin: 0;
  max-width: 400px;
}

/* Header */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #004879;
  border-radius: 10px 10px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.header-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: normal;
  color: #FFFFFF;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.close-button:hover {
  opacity: 0.8;
}

/* Content */
.dialog-content {
  padding: 16px 8px 16px;
  background: #F9FBFD;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #4A4A4A;
  margin: 0;
}

/* Patients Section */
.patients-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.patients-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0 4px 0;
}

.manage-usage-button-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Patient List */
.patient-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  overflow-y: auto;
  padding: 24px 0;
  margin-top: 12px;
  border-radius: 10px;
  background-color: white;
  min-height: 180px;
}

.patient-list-container {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
  gap: 12px;
  width: 100%;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
}

.empty-icon {
  width: 189px;
  height: 180px;
  margin-bottom: 16px;
}

.empty-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #666666;
  margin: 0;
}

/* Patient Item */
.patient-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #FFFFFF;
  border-radius: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid #E5E7EB;
  transition: all 0.3s ease;
  width: 100%;
}

.patient-item:last-child {
  margin-bottom: 0;
}

/* Transition animations for patient list */
.patient-list-enter-active {
  transition: all 0.3s ease;
}

.patient-list-leave-active {
  transition: all 0.3s ease;
  position: absolute;
  width: calc(100% - 32px);
}

.patient-list-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.patient-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.patient-list-move {
  transition: transform 0.3s ease;
}

.patient-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.patient-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #E8E8E8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  color: #979797;
}

.patient-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.patient-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.patient-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333333;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.patient-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666666;
}

.patient-id {
  white-space: nowrap;
}

.patient-added {
  white-space: nowrap;
}

/* Progress Section - Two bars side by side */
.progress-section {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  width: 100%;
}

.progress-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.progress-label {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #333333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: #E0E0E0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #004879;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.patient-actions-group {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}


/* Add Patient View Styles */
.content-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

/* Custom Patient Option Styles for MultiSelector */
.patient-option-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0;
}

.patient-option-custom.disabled {
  opacity: 0.6;
}

.patient-option-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.patient-option-name {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #333333;
}

.patient-option-meta {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #666666;
}

.patient-option-disabled-reason {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: #D10000;
}

.patient-option-disabled-reason svg {
  flex-shrink: 0;
}

/* Confirm View Styles */
.confirmation-message {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #4A4A4A;
  margin: 0 0 16px 0;
  padding: 0 16px;
}

.patient-list-container {
  padding: 0 16px;
}

.confirm-patient-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #FFFFFF;
  border-radius: 10px;
  padding: 16px;
}

.confirm-patient-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-bottom: 1px solid #E8E8E8;
}

.confirm-patient-item:last-child {
  border-bottom: none;
}

.confirm-patient-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #333333;
}

.patient-meta-rows {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #666666;
  margin-top: 4px;
}

.confirm-delete-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.confirm-delete-button:hover {
  background: #FEE2E2;
}

.confirm-delete-button svg {
  width: 13px;
  height: 13px;
}

/* Footer */
.dialog-footer {
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: #F9FBFD;
  border-radius: 0 0 10px 10px;
}

.footer-divider {
  height: 1px;
  background: #C4C4C4;
  border-style: dashed;
  margin-bottom: 24px;
}

.footer-actions {
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  padding-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* Override footer border for add-patient and confirm views */
.dialog-footer .footer-divider + .footer-actions {
  border-top: none;
  padding-top: 0;
}
</style>
